import React, { useCallback, useMemo, useState } from 'react';

import { type Event, EventTypeEnum } from '@clubspark-node/clubspark-tennis-types';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { tournamentEngine } from 'tods-competition-factory';

import { localTimeZoneAtom, tournamentIdAtom } from 'atoms/tournament';
import {
  ADULT,
  FAMILY,
  INDIVIDUAL,
  JUNIOR,
  MAX_BATCH_SIZE,
  MAX_WTN_BATCH_SIZE,
  MIXED,
  SEEDING,
  STANDING,
  TEAM,
  WHEELCHAIR,
} from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/constants';
import {
  type FormatType,
  ListTypeEnum,
  type Option,
} from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import RankingsAndRatingsComponent from 'components/panels/eventsSplitDraws/selectedEvent/RankingsAndRatingsComponent';
import {
  getAgeRestriction,
  getAvailablePublishDates,
  mapPtDataToScaleItems,
  mapRankingDataToScaleItems,
  mapWtnDataToScaleItems,
} from 'components/panels/eventsSplitDraws/selectedEvent/utils';
import TMXCircularProgress from 'components/progress/TMXCircularProgress';
import { getClientConfig } from 'config/clients';
import { TOURNAMENT_ENGINE } from 'config/graphql/constants';
import { RankingsAndRatingsContext } from 'context/rankingsAndRatingsContext';
import { useRankingsAndRatingsService } from 'hooks/rankingsAndRatings/useRankingsAndRatingsService';
import { useSaveTournament } from 'hooks/tournament/useSaveTournament';
import useTournamentParticipants from 'hooks/tournament/useTournamentParticipants';
import { type PtData } from 'typedefs/playTrackerTypes';
import { type WtnResult } from 'typedefs/wtnTypes';
import { factorySafeTimeToDayjs } from 'utils/dateTimeUtils';
import {
  getParticipantsNationalIds,
  getParticipantsOrganisationIds,
  getParticipantsPersonIds,
} from 'utils/participant';
import { type ScalingType } from 'utils/scalings';
import { getNtrpLevel } from 'utils/scalings/ntrp';
import { getCustomLevel, isCustomEvent } from 'utils/scalings/ranking';

export enum wheelchairRatingEnum {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  Q = 'QUAD',
}

enum GenderEnum {
  MALE = 'M',
  FEMALE = 'F',
  MIXED = 'X',
  COED = 'C',
}

enum ReverseGenderEnum {
  M = 'MALE',
  F = 'FEMALE',
  X = 'MIXED',
  C = 'COED',
}

interface RankingsAndRatingsContainerProps {
  scalesToHide: ScalingType[];
  event?: Event;
}

const RankingsAndRatingsContainer: React.FC<React.PropsWithChildren<RankingsAndRatingsContainerProps>> = React.memo(
  ({ scalesToHide, event }) => {
    const tournamentId = useAtomValue(tournamentIdAtom);
    const localTimeZone = useAtomValue(localTimeZoneAtom);
    const rankingsAndRatingsService = useRankingsAndRatingsService();
    const { tournamentParticipants } = useTournamentParticipants();
    const eventId = event?.eventId;

    const divisionData = useMemo(
      () => event?.extensions?.find((e) => e.name === 'division')?.value,
      [event?.extensions],
    );
    const isFamily = !(typeof divisionData?.familyType === 'undefined' || divisionData?.familyType === null);
    const familyCategory = isFamily ? divisionData?.familyType : null;
    const categoryName = isFamily
      ? familyCategory
      : event?.category?.categoryName || event?.category?.ageCategoryCode || event?.eventId;
    const isJunior = event?.category?.subType === JUNIOR;
    const itemEventType = isFamily ? 'TEAM' : 'SINGLES';

    const rankingItemType = `RETRIEVAL.RANKING.${itemEventType}.${categoryName}`;
    const ratingItemType = `RETRIEVAL.RATING.SINGLES.NTRP`;
    const wtnItemType = `RETRIEVAL.RATING.SINGLES.WTN`;
    const ptItemType = `RETRIEVAL.RATING.SINGLES.PT`;
    const mensRankingItemType = `RETRIEVAL.RANKING.SINGLES.MALE${categoryName}`;
    const womensRankingItemType = `RETRIEVAL.RANKING.SINGLES.FEMALE${categoryName}`;

    const isDoubles = event?.eventType === EventTypeEnum.DOUBLES;
    const isTeam = event?.eventType === EventTypeEnum.TEAM;
    const isWheelchair = !!divisionData?.wheelchairRating;
    const wheelchairRating = isWheelchair ? wheelchairRatingEnum[divisionData?.wheelchairRating] : null;
    const isJuniorWheelchair = isWheelchair && event?.category?.ageCategoryCode === 'U18';
    const ageRestriction = getAgeRestriction(event?.category, isJunior || isJuniorWheelchair);
    const isNTRP = !(typeof divisionData?.ratingCategory === 'undefined' || divisionData?.ratingCategory === null);
    const isNtrpCombo = isNTRP ? divisionData?.ratingCategory?.ratingCategoryType === 'COMBINED' : false;
    const eventNtrpRating =
      typeof divisionData?.ratingCategory === 'undefined' ? null : getNtrpLevel(divisionData?.ratingCategory);
    const isMixed = divisionData?.gender === 'MIXED' || divisionData?.gender === 'COED';

    const rankingsEventParticipants = tournamentParticipants.filter(({ participantId }) =>
      (event?.entries || []).map(({ participantId }) => participantId)?.includes(participantId),
    );

    const eventParticipants = useMemo(
      () =>
        isDoubles || isTeam
          ? rankingsEventParticipants
              .map((p) => (p?.individualParticipantIds?.length > 0 ? p.individualParticipants : p))
              ?.flat()
          : rankingsEventParticipants,
      [isDoubles, isTeam, rankingsEventParticipants],
    );

    const eventParticipantsUstaIds: any[] = useMemo(
      () => getParticipantsOrganisationIds(eventParticipants, isDoubles),
      [eventParticipants, isDoubles],
    );

    const rankingParticipantUstaIds: any[] = useMemo(
      () => getParticipantsOrganisationIds(rankingsEventParticipants, isDoubles),
      [rankingsEventParticipants, isDoubles],
    );

    const getWtnNationalIds = useMemo(() => {
      const personIdAsNationalId = getClientConfig().scalingTypes.some((configItem) => {
        return configItem[0] == 'WTN' && configItem[1]?.personIdAsNationalId;
      });
      return personIdAsNationalId ? getParticipantsPersonIds : getParticipantsNationalIds;
    }, []);

    const eventParticipantsNationalIds: string[] = useMemo(
      () => getWtnNationalIds(eventParticipants, isDoubles),
      [eventParticipants, isDoubles, getWtnNationalIds],
    );

    const tournamentParticipantsNationalIds: string[] = useMemo(
      () => getWtnNationalIds(tournamentParticipants, isDoubles),
      [tournamentParticipants, isDoubles, getWtnNationalIds],
    );

    // TODO: COMP-8470 - Rework this
    const [{ getRankingsListData, getRankingsListLoading }, setRankingsListQuery] = useState<{
      getRankingsListData: any; // TODO: add type - by vuk_niko
      getRankingsListLoading: boolean;
    }>({ getRankingsListData: undefined, getRankingsListLoading: false });
    const [{ rankingsAndRatingsLoading }, setRankingsAndRatingsQuery] = useState<{
      rankingsAndRatingsLoading: boolean;
    }>({
      rankingsAndRatingsLoading: false,
    });
    const [{ wtnRatingsLoading }, setWtnRatingsQuery] = useState<{ wtnRatingsLoading: boolean }>({
      wtnRatingsLoading: false,
    });
    const [{ ptPointsLoading }, setPtPointsQuery] = useState<{ ptPointsLoading: boolean }>({
      ptPointsLoading: false,
    });

    const [selectedPublishDate, setSelectedPublishDate] = useState('0');
    const [availableDates, setAvailableDates] = useState<Option[]>();
    const [format, setFormat] = useState<string>(
      isFamily || isWheelchair
        ? EventTypeEnum.DOUBLES
        : isMixed
          ? MIXED
          : isDoubles
            ? EventTypeEnum.DOUBLES
            : EventTypeEnum.SINGLES,
    );
    const [formatType, setFormatType] = useState<FormatType>(isFamily ? TEAM : INDIVIDUAL);
    const [listType, setListType] = useState<ListTypeEnum>(isJunior ? ListTypeEnum.SEEDING : ListTypeEnum.STANDING);
    const [gender, setGender] = useState<string>(isFamily ? GenderEnum.MIXED : GenderEnum.MALE);
    const [loadingList, setLoadingList] = useState<boolean>(false);

    // TODO: COMP-8470 - Rework this
    const rankingTimeItem = useMemo(
      () =>
        eventId &&
        tournamentEngine.getTimeItem({
          itemType: rankingItemType,
          eventId,
        }).timeItem,
      [eventId, rankingItemType],
    );

    const ratingTimeItem = useMemo(
      () =>
        eventId &&
        tournamentEngine.getTimeItem({
          itemType: ratingItemType,
          eventId,
        }).timeItem,
      [eventId, ratingItemType],
    );

    const wtnTimeItem = useMemo(
      () =>
        tournamentEngine.getTimeItem({
          itemType: wtnItemType,
        }).timeItem,
      [wtnItemType],
    );

    const mensRankingTimeItem = useMemo(
      () =>
        eventId &&
        tournamentEngine.getTimeItem({
          itemType: mensRankingItemType,
          eventId,
        }).timeItem,
      [eventId, mensRankingItemType],
    );

    const womensRankingTimeItem = useMemo(
      () =>
        eventId &&
        tournamentEngine.getTimeItem({
          itemType: womensRankingItemType,
          eventId,
        }).timeItem,
      [eventId, womensRankingItemType],
    );

    const ptTimeItem = useMemo(
      () =>
        eventId &&
        tournamentEngine.getTimeItem({
          itemType: ptItemType,
          eventId,
        }).timeItem,
      [eventId, ptItemType],
    );

    const { action, saving, saveTournament } = useSaveTournament();

    const handleGetRankingsList = useCallback(
      async (
        listTypeVal: string = listType,
        formatVal: string = format,
        formatTypeVal: string = formatType,
        genderVal: string = gender,
      ) => {
        const applyWheelchairRating = isWheelchair && divisionData?.gender === 'COED' && !isJuniorWheelchair;
        const ranklistFilter = {
          listType: isJunior ? listTypeVal : STANDING,
          matchFormat:
            formatVal === MIXED
              ? EventTypeEnum.DOUBLES
              : listTypeVal === SEEDING
                ? formatVal
                : !isJunior
                  ? formatVal
                  : null,
          matchFormatType: isFamily
            ? EventTypeEnum.TEAM
            : formatTypeVal === TEAM || formatVal === EventTypeEnum.DOUBLES
              ? formatTypeVal
              : null,
          playerType: isWheelchair ? WHEELCHAIR : isJunior ? JUNIOR : isFamily ? FAMILY : ADULT,
          playerLevel: applyWheelchairRating
            ? wheelchairRating
            : isFamily
              ? null
              : event?.category?.ageCategoryCode === 'OPEN' && !isWheelchair
                ? 'Op'
                : isNTRP
                  ? eventNtrpRating
                  : null,
          visible: true,
          gender:
            isJuniorWheelchair || (isWheelchair && applyWheelchairRating)
              ? GenderEnum.COED
              : isMixed && (formatVal === MIXED || isFamily)
                ? GenderEnum.MIXED
                : isMixed && formatVal !== MIXED
                  ? genderVal
                  : GenderEnum[event?.gender],
          genderModifier: isJuniorWheelchair ? null : isMixed && formatVal === MIXED && !isFamily ? genderVal : null,
          familyCategory: isFamily ? familyCategory : null,
          publishDate: {
            between: {
              start: dayjs()
                .tz(localTimeZone ?? 'UTC')
                .startOf('minute')
                .subtract(28, 'day'),
              end: dayjs()
                .tz(localTimeZone ?? 'UTC')
                .startOf('minute'),
            },
          },
          ...(ageRestriction !== '' && { ageRestriction }),
        };

        setRankingsListQuery({ getRankingsListData: undefined, getRankingsListLoading: true });
        const rankingsListResult = await rankingsAndRatingsService.getRankingsList(ranklistFilter);
        if (rankingsListResult.data) {
          const dates = getAvailablePublishDates(rankingsListResult.data?.ranklists);
          setAvailableDates(dates);
          setSelectedPublishDate(dates[0]?.value);
          setRankingsListQuery({ getRankingsListData: rankingsListResult.data, getRankingsListLoading: false });
        }
      },
      [
        ageRestriction,
        divisionData?.gender,
        event?.category?.ageCategoryCode,
        event?.gender,
        eventNtrpRating,
        familyCategory,
        format,
        formatType,
        gender,
        isFamily,
        isJunior,
        isJuniorWheelchair,
        isMixed,
        isNTRP,
        isWheelchair,
        listType,
        rankingsAndRatingsService,
        localTimeZone,
        wheelchairRating,
      ],
    );

    // TODO: COMP-8470 - Rework this
    const handleSetParticipantRankingScaleItems = useCallback(
      async (data: any, publishDate: string) => {
        const payload = data || [];
        const methods = [];
        const scaleItemsWithParticipantIds = payload
          .map((participant) => {
            if (!participant) {
              return;
            }
            const { participantId, ranking } = participant;
            const scaleItems = [];
            if (ranking?.itemValue) {
              const {
                itemValue: { ageCategoryCode, eventSubType, ranking: scaleValue },
              } = ranking;

              const ratingCategory = event?.extensions?.find((e) => e.name === 'division')?.value?.ratingCategory;
              scaleItems.push({
                scaleValue,
                scaleName: `${ageCategoryCode}${isCustomEvent(ratingCategory) ? getCustomLevel(ratingCategory) : ''}`,
                scaleType: 'RANKING',
                eventType: eventSubType,
                scaleDate: dayjs()
                  .tz(localTimeZone ?? 'UTC')
                  .format('YYYY-MM-DDT00:00'),
              });
            }
            return { participantId, scaleItems };
          })
          .filter(Boolean);

        if (scaleItemsWithParticipantIds.length) {
          const method = { method: 'setParticipantScaleItems', params: { scaleItemsWithParticipantIds } };
          methods.push(method);
        }
        const rankingTimeItemName = isFamily ? familyCategory : event?.category?.ageCategoryCode;
        const mixedEventTimeItemName = isMixed && !isFamily && !isWheelchair ? ReverseGenderEnum[gender] : '';
        const rankingTimeItem = {
          itemType: `RETRIEVAL.RANKING.${itemEventType}.${mixedEventTimeItemName + rankingTimeItemName}`,
          itemValue: factorySafeTimeToDayjs(publishDate, localTimeZone).format('YYYY-MM-DDTHH:mm'),
        };

        const addRankingTimeItem = { method: 'addEventTimeItem', params: { eventId, timeItem: rankingTimeItem } };
        methods.push(addRankingTimeItem);
        await saveTournament('addRatingsAndRankings', TOURNAMENT_ENGINE, methods);
      },
      [
        isFamily,
        familyCategory,
        event?.category?.ageCategoryCode,
        event?.extensions,
        isMixed,
        isWheelchair,
        gender,
        itemEventType,
        localTimeZone,
        eventId,
        saveTournament,
      ],
    );

    const handleGetRankingList = useCallback(
      async (id?: string, resetOffset?: false) => {
        let offset = 0;
        let getRankingListData = [];

        const getNextRankingListChunk = async (id?: string, rankingListResult?: any, incrementOffset?: boolean) => {
          if (incrementOffset) {
            offset = getRankingListData.length;
          }
          const newitemPageArgs = {
            skip: offset,
            limit: MAX_BATCH_SIZE,
          };
          const newRankingListResult = await rankingsAndRatingsService.getRankingList(
            selectedPublishDate,
            newitemPageArgs,
          );
          if (newRankingListResult.data) {
            const newResults = [...getRankingListData, ...(newRankingListResult.data?.ranklist?.rankingItems ?? [])];
            getRankingListData = newResults;
            const isTeamFormat = newRankingListResult.data?.ranklist?.matchFormatType === TEAM;
            if (newResults?.length === newRankingListResult.data?.ranklist?.itemCount) {
              const rankings = mapRankingDataToScaleItems(
                newResults,
                event,
                eventParticipants,
                rankingParticipantUstaIds,
                eventParticipantsUstaIds,
                ReverseGenderEnum[gender],
                isMixed,
                isTeamFormat,
                isWheelchair,
              )?.filter((ranking) => ranking !== null);
              await handleSetParticipantRankingScaleItems(rankings, newRankingListResult.data?.ranklist?.publishDate);
              setLoadingList(false);
            } else {
              getNextRankingListChunk(id, newRankingListResult, true);
            }
          }
        };

        if (resetOffset) {
          offset = 0;
          getRankingListData = [];
        }
        const itemPageArgs = {
          skip: offset,
          limit: MAX_BATCH_SIZE,
        };
        const rankingListResult = await rankingsAndRatingsService.getRankingList(id, itemPageArgs, 'cache-first');

        if (rankingListResult.data) {
          offset = getRankingListData.length + rankingListResult.data?.ranklist?.rankingItems?.length;
          getRankingListData = rankingListResult.data?.ranklist?.rankingItems;
          getNextRankingListChunk(id, rankingListResult, false);
        }
      },
      [
        event,
        eventParticipants,
        eventParticipantsUstaIds,
        rankingParticipantUstaIds,
        gender,
        handleSetParticipantRankingScaleItems,
        isMixed,
        isWheelchair,
        rankingsAndRatingsService,
        selectedPublishDate,
      ],
    );

    // TODO: COMP-8470 - Rework this
    const setParticipantRatingScaleItems = useCallback(
      async (data: any) => {
        const payload = data?.rankingsAndRatings?.data || [];
        const methods = [];

        const scaleItemsWithParticipantIds = payload
          .map((participant) => {
            if (!participant) return;
            const { participantId, rating } = participant;
            const scaleItems = [];
            if (rating?.itemValue) {
              const {
                itemValue: { eventSubType: ratingSubType },
              } = rating;
              scaleItems.push({
                scaleValue: { ...rating?.itemValue },
                scaleName: 'NTRP',
                scaleType: 'RATING',
                eventType: ratingSubType,
                scaleDate: dayjs()
                  .tz(localTimeZone ?? 'UTC')
                  .format('YYYY-MM-DDT00:00'),
              });
            }
            return { participantId, scaleItems };
          })
          .filter((f) => f);

        if (scaleItemsWithParticipantIds.length) {
          const method = {
            method: 'setParticipantScaleItems',
            params: { removePriorValues: true, scaleItemsWithParticipantIds },
          };
          methods.push(method);
        }

        const ratingTimeItem = {
          itemType: `RETRIEVAL.RATING.SINGLES.NTRP`,
          itemValue: dayjs()
            .tz(localTimeZone ?? 'UTC')
            .format('YYYY-MM-DDTHH:mm'),
        };

        const addRatingTimeItem = { method: 'addEventTimeItem', params: { eventId, timeItem: ratingTimeItem } };
        methods.push(addRatingTimeItem);
        await saveTournament('addRatingsAndRankings', TOURNAMENT_ENGINE, methods);
      },
      [localTimeZone, eventId, saveTournament],
    );

    const handleGetRankingsAndRatings = useCallback(async () => {
      setRankingsAndRatingsQuery({ rankingsAndRatingsLoading: true });
      const rankingsAndRatingsResult = await rankingsAndRatingsService.getRankingsAndRatings(eventId, 'network-only');
      if (rankingsAndRatingsResult.data) {
        await setParticipantRatingScaleItems(rankingsAndRatingsResult.data);
      }
      setRankingsAndRatingsQuery({ rankingsAndRatingsLoading: false });
    }, [eventId, rankingsAndRatingsService, setParticipantRatingScaleItems]);

    // TODO: COMP-8470 - Rework this
    const handleSetParticipantWtnRatingScaleItems = useCallback(
      async (data: any) => {
        const payload = data || [];
        const methods = [];

        const scaleItemsWithParticipantIds = payload
          .map((participant) => {
            if (!participant) return;
            const { participantId, wtnRating } = participant;
            const scaleItems = [];
            wtnRating.map((rating) => {
              if (rating?.itemValue?.wtnRating) {
                scaleItems.push({
                  scaleValue: { ...rating.itemValue },
                  scaleName: 'WTN',
                  scaleType: 'RATING',
                  eventType: rating.eventType,
                  scaleDate: rating.itemDate,
                });
              }
            });
            return { participantId, scaleItems };
          })
          .filter((f) => f);

        if (scaleItemsWithParticipantIds.length) {
          const method = {
            method: 'setParticipantScaleItems',
            params: {
              scaleItemsWithParticipantIds,
              removePriorValues: true, // Only the most recent tournament-wide WTNs are relevant
            },
          };
          methods.push(method);
        }

        const ratingTimeItem = {
          itemType: `RETRIEVAL.RATING.SINGLES.WTN`,
          itemValue: dayjs()
            .tz(localTimeZone ?? 'UTC')
            .format('YYYY-MM-DDTHH:mm'),
        };
        const addRatingTimeItem = {
          method: 'addTournamentTimeItem',
          params: { tournamentId, timeItem: ratingTimeItem },
        };
        methods.push(addRatingTimeItem);
        await saveTournament('addRatingsAndRankings', TOURNAMENT_ENGINE, methods);
      },
      [localTimeZone, tournamentId, saveTournament],
    );

    const handleGetWtnRating = useCallback(async () => {
      let getWtnData: WtnResult = { worldTennisNumber: [] };

      const getWtnChunk = async (nationalIdsSet: any[], currentRun: number, numberOfRuns: number) => {
        const wtnRatingResult = await rankingsAndRatingsService.getWtnRating(
          nationalIdsSet[currentRun - 1],
          'network-only',
        );
        let runNumber = currentRun;

        if (wtnRatingResult.data) {
          const newResult = [...getWtnData.worldTennisNumber, ...(wtnRatingResult.data?.worldTennisNumber ?? [])];
          getWtnData.worldTennisNumber = newResult;

          if (currentRun < numberOfRuns) {
            runNumber = runNumber + 1;
            getWtnChunk(nationalIdsSet, runNumber, numberOfRuns);
          } else {
            setWtnRatingsQuery({ wtnRatingsLoading: false });
            const wtnEventTypes = [EventTypeEnum.SINGLES, EventTypeEnum.DOUBLES];
            const wtnRatings = mapWtnDataToScaleItems(getWtnData, wtnEventTypes, tournamentParticipants);
            await handleSetParticipantWtnRatingScaleItems(wtnRatings);
          }
        }
      };

      setWtnRatingsQuery({ wtnRatingsLoading: true });
      let noOfWtnCalls = 1;
      const nationalIdChunks = [];
      let i = 0;
      let j = 0;

      getWtnData = { worldTennisNumber: [] };

      const participantsNationalIds = !event ? tournamentParticipantsNationalIds : eventParticipantsNationalIds;
      if (participantsNationalIds.length > MAX_WTN_BATCH_SIZE) {
        noOfWtnCalls = Math.ceil(participantsNationalIds.length / MAX_WTN_BATCH_SIZE);
        for (i = 0, j = participantsNationalIds.length; i < j; i += MAX_WTN_BATCH_SIZE) {
          nationalIdChunks.push(participantsNationalIds.slice(i, i + MAX_WTN_BATCH_SIZE));
        }
      } else {
        nationalIdChunks[0] = participantsNationalIds;
      }

      getWtnChunk(nationalIdChunks, 1, noOfWtnCalls);
    }, [
      event,
      tournamentParticipantsNationalIds,
      eventParticipantsNationalIds,
      rankingsAndRatingsService,
      tournamentParticipants,
      handleSetParticipantWtnRatingScaleItems,
    ]);

    // TODO: COMP-8470 - Rework this
    const handleSetParticipantPtPointsScaleItems = useCallback(
      async (data: any) => {
        const payload = data || [];
        const methods = [];

        const scaleItemsWithParticipantIds = payload
          .map((participant) => {
            if (!participant) return;
            const { participantId, ptPoints } = participant;
            const scaleItems =
              ptPoints?.itemValue?.map((scaleValue) => ({
                scaleValue,
                scaleName: `PT.${scaleValue.ballColour.toUpperCase()}`,
                scaleType: 'RATING',
                eventType: event?.eventType,
                scaleDate: dayjs()
                  .tz(localTimeZone ?? 'UTC')
                  .format('YYYY-MM-DDT00:00'),
              })) ?? [];

            return { participantId, scaleItems };
          })
          .filter((f) => f);

        if (scaleItemsWithParticipantIds.length) {
          const method = {
            method: 'setParticipantScaleItems',
            params: { scaleItemsWithParticipantIds, removePriorValues: true },
          };
          methods.push(method);
        }

        const ptTimeItem = {
          itemType: ptItemType,
          itemValue: dayjs()
            .tz(localTimeZone ?? 'UTC')
            .format('YYYY-MM-DDTHH:mm'),
        };

        const addPtTimeItem = { method: 'addEventTimeItem', params: { eventId, timeItem: ptTimeItem } };
        methods.push(addPtTimeItem);

        await saveTournament('addRatingsAndRankings', TOURNAMENT_ENGINE, methods);
      },
      [ptItemType, localTimeZone, eventId, saveTournament, event?.eventType],
    );

    const handleGetPtPoints = useCallback(async () => {
      let getPtData: PtData = { points: [] };

      const getPtChunk = async (eventParticipantsNationalIds: any[], currentRun: number, numberOfRuns: number) => {
        const ptPointsResult = await rankingsAndRatingsService.getPtPoints(
          eventParticipantsNationalIds[currentRun - 1],
          'network-only',
        );

        let runNumber = currentRun;
        if (ptPointsResult?.data) {
          const newResult = [...getPtData.points, ...(ptPointsResult.data?.activityProfiles?.items ?? [])];
          getPtData.points = newResult;

          if (currentRun < numberOfRuns) {
            runNumber = runNumber + 1;
            getPtChunk(eventParticipantsNationalIds, runNumber, numberOfRuns);
          } else {
            setPtPointsQuery({ ptPointsLoading: false });
            const ptPoints = mapPtDataToScaleItems(getPtData, eventParticipants, eventParticipantsUstaIds?.flat());
            await handleSetParticipantPtPointsScaleItems(ptPoints);
          }
        } else {
          setPtPointsQuery({ ptPointsLoading: false });
        }
      };

      setPtPointsQuery({ ptPointsLoading: true });
      let noOfPtCalls = 1;
      const nationalIdChunks = [];
      let i = 0;
      let j = 0;

      getPtData = { points: [] };

      if (eventParticipantsNationalIds.length > MAX_BATCH_SIZE) {
        noOfPtCalls = Math.ceil(eventParticipantsNationalIds.length / MAX_BATCH_SIZE);
        for (i = 0, j = eventParticipantsNationalIds.length; i < j; i += MAX_BATCH_SIZE) {
          nationalIdChunks.push(eventParticipantsNationalIds.slice(i, i + MAX_BATCH_SIZE));
        }
      } else {
        nationalIdChunks[0] = eventParticipantsNationalIds;
      }

      getPtChunk(nationalIdChunks, 1, noOfPtCalls);
    }, [
      eventParticipants,
      eventParticipantsUstaIds,
      handleSetParticipantPtPointsScaleItems,
      eventParticipantsNationalIds,
      rankingsAndRatingsService,
    ]);

    return useMemo(() => {
      const rankingsConfig = {
        availableDates,
        format,
        formatType,
        gender,
        isDoubles,
        isFamily,
        familyCategory,
        isWheelchair,
        wheelchairRating,
        isJunior,
        isNTRP,
        isNtrpCombo,
        isMixed,
        listType,
        loadingList,
        selectedPublishDate,
        setFormat,
        setFormatType,
        setListType,
        setGender,
        setLoadingList,
        setSelectedPublishDate,
      };

      return rankingsAndRatingsLoading ||
        wtnRatingsLoading ||
        ptPointsLoading ||
        (saving && action === 'addRatingsAndRankings') ? (
        <TMXCircularProgress />
      ) : (
        <RankingsAndRatingsContext.Provider
          value={{
            rankingsConfig,
            getRankingsListData,
            getRankingsListLoading,
            getRankingList: handleGetRankingList,
            getRankingsList: handleGetRankingsList,
            getRankingsAndRatings: handleGetRankingsAndRatings,
            getWtnRating: handleGetWtnRating,
            getPtPoints: handleGetPtPoints,
          }}
        >
          <RankingsAndRatingsComponent
            event={event}
            localTimeZone={localTimeZone}
            mensRankingTimeItem={mensRankingTimeItem}
            ptTimeItem={ptTimeItem}
            rankingTimeItem={rankingTimeItem}
            ratingTimeItem={ratingTimeItem}
            scalesToHide={scalesToHide || []}
            womensRankingTimeItem={womensRankingTimeItem}
            wtnTimeItem={wtnTimeItem}
          />
        </RankingsAndRatingsContext.Provider>
      );
    }, [
      availableDates,
      format,
      formatType,
      gender,
      isDoubles,
      isFamily,
      familyCategory,
      isWheelchair,
      wheelchairRating,
      isJunior,
      isNTRP,
      isNtrpCombo,
      isMixed,
      listType,
      loadingList,
      selectedPublishDate,
      rankingsAndRatingsLoading,
      wtnRatingsLoading,
      ptPointsLoading,
      saving,
      action,
      getRankingsListData,
      getRankingsListLoading,
      handleGetRankingList,
      handleGetRankingsList,
      handleGetRankingsAndRatings,
      handleGetWtnRating,
      handleGetPtPoints,
      rankingTimeItem,
      ratingTimeItem,
      wtnTimeItem,
      mensRankingTimeItem,
      womensRankingTimeItem,
      ptTimeItem,
      event,
      scalesToHide,
      localTimeZone,
    ]);
  },
);

export default RankingsAndRatingsContainer;
